import { Component, HostListener, OnInit, Optional } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { PersonalApiKeyService } from 'src/app/services/personal-api-key.service';
// Modified by Karanam on Oct 09 2026 to fix code review comment S-09/A-08: rights on function 564 drive which controls are enabled.
import { GeneralService } from 'src/app/services/general.service';
import { EUserFunctions } from 'src/app/Shared/enum';

@Component({
  // Modified by Karanam on Oct 09 2026 to fix code review comment F-01: component renamed from PersonalApiKeyPlaceholderComponent (it is no longer a placeholder); files moved to Shared/personal-api-key. Original selector / template / style lines commented out below.
  // selector: 'app-personal-api-key-placeholder',
  // templateUrl: './personal-api-key-placeholder.component.html',
  // styleUrls: ['./personal-api-key-placeholder.component.scss'],
  selector: 'app-personal-api-key',
  templateUrl: './personal-api-key.component.html',
  styleUrls: ['./personal-api-key.component.scss'],
  standalone: false
})
// export class PersonalApiKeyPlaceholderComponent implements OnInit {
export class PersonalApiKeyComponent implements OnInit {
  public isModalOpen = true;
  public isModalHostMode = false;
  // Modified by Karanam on Oct 09 2026 to fix code review comment S-17/S-18: admin/targetUserId variant removed; keys are self-service only.
  // public targetUserId: number | null = null;
  public tokenLabel = '';
  public expirySelection = '365';
  public generatedToken = '';
  public isKeyCopied = false;
  public isGenerating = false;
  public isLoading = false;
  public revokingTokenId: number | null = null;
  public tokens: any[] = [];
  public expiryOptions = [
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
    { value: '180', label: '6 months' },
    { value: '365', label: '1 year' },
    { value: '730', label: '2 years' }
  ];

  private toasterConfig = {
    closeButton: true,
    timeOut: 3500,
    easing: 'ease-out',
    progressBar: true,
    positionClass: 'toast-top-right',
    tapToDismiss: true,
    showEasing: 'swing',
    hideEasing: 'linear',
    showMethod: 'fadeIn',
    hideMethod: 'slideUp'
  };

  constructor(
    private personalApiKeyService: PersonalApiKeyService,
    private toastr: ToastrService,
    private router: Router,
    private location: Location,
    @Optional() private bsModalRef: BsModalRef,
    // Modified by Karanam on Oct 09 2026 to fix code review comment S-09/A-08: inject GeneralService to read the user's rights.
    private generalService: GeneralService
  ) { }

// Modified by Karanam on Oct 09 2026 to fix code review comment S-09/A-08: rights on function 564 (User - Generate Personal Access Token). View-only users see the keys but Generate and Revoke are disabled. When an admin manages another user's keys (targetUserId set) the server enforces User/EditRight instead, so the controls stay enabled.
  private get pat564Rights(): any {
    return this.generalService.getUserPermissionsForModule(EUserFunctions['User - Generate Personal Access Token']) || {};
  }

  // Modified by Karanam on Oct 09 2026 to fix code review comment S-17/S-18: no targetUserId; rights on function 564 only.
  // get canViewKeys(): boolean { return !!this.targetUserId || !!this.pat564Rights.ViewRight; }
  get canViewKeys(): boolean { return !!this.pat564Rights.ViewRight; }
  // get canGenerateKey(): boolean { return !!this.targetUserId || !!this.pat564Rights.CreateRight; }
  get canGenerateKey(): boolean { return !!this.pat564Rights.CreateRight; }
  // Modified by Karanam on Oct 09 2026 to fix code review comment S-09/A-08: the Delete right is disabled for function 564 in the user group rights UI, so Revoke now follows CreateRight (same as Generate). Original getter commented out below.
  // get canRevokeKey(): boolean { return !!this.targetUserId || !!this.pat564Rights.DeleteRight; }
  // get canRevokeKey(): boolean { return !!this.targetUserId || !!this.pat564Rights.CreateRight; }
  get canRevokeKey(): boolean { return !!this.pat564Rights.CreateRight; }

  ngOnInit(): void {
    // If the component is created by BsModalService, force host mode to avoid route navigation on close.
    this.isModalHostMode = this.isModalHostMode || !!this.bsModalRef;
    this.loadTokens();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent): void {
    if (!this.hasPendingChanges()) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  }

  onBackdropClick(): void {
    this.closeModal();
  }

  // Modified by Karanam on Oct 09 2026 to fix code review comment F-01: open the FAQ page in a new tab so the key dialog stays open and no unsaved label or uncopied key is lost.
  openFaq(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    const url = this.location.prepareExternalUrl(this.router.serializeUrl(this.router.createUrlTree(['/personal-api-key/faq'])));
    window.open(url, '_blank', 'noopener');
  }

  closeModal(): void {
    if (!this.canCloseWindow()) {
      return;
    }

    this.isModalOpen = false;

    // Always prefer in-place modal close when running under ngx-bootstrap modal host.
    if (this.bsModalRef) {
      this.bsModalRef.hide();
      return;
    }

    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    this.router.navigate(['/dashboard']);
  }

  loadTokens(): void {
    // Modified by Karanam on Oct 09 2026 to fix code review comment S-09/A-08: do not call the list endpoint without View on function 564 (it would return 403).
    if (!this.canViewKeys) {
      this.tokens = [];
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    // Modified by Karanam on Oct 09 2026 to fix code review comment S-17/S-18: no targetUserId.
    // this.personalApiKeyService.getTokens(this.targetUserId || undefined).subscribe(response => {
    this.personalApiKeyService.getTokens().subscribe(response => {
      this.tokens = response || [];
      this.isLoading = false;
    }, (error) => {
      this.isLoading = false;
      this.toastr.error(this.getErrorMessage(error, 'Unable to load API keys.'), 'Error', this.toasterConfig);
    });
  }

  generateToken(): void {
    // Modified by Karanam on Oct 09 2026 to fix code review comment S-09/A-08: guard in addition to the disabled button.
    if (!this.canGenerateKey) {
      return;
    }

    const label = (this.tokenLabel || '').trim();
    // Client-side validation keeps UX responsive before API call.
    if (!label) {
      this.toastr.warning('Token label is required.', 'Validation', this.toasterConfig);
      return;
    }

    if (label.length > 100) {
      this.toastr.warning('Token label cannot exceed 100 characters.', 'Validation', this.toasterConfig);
      return;
    }

    const expiryUtcIso = this.getExpiryDateUtcIso();
    this.isGenerating = true;
    // Modified by Karanam on Oct 09 2026 to fix code review comment S-17/S-18: no targetUserId.
    // this.personalApiKeyService.generateToken(label, expiryUtcIso, this.targetUserId || undefined).subscribe(response => {
    this.personalApiKeyService.generateToken(label, expiryUtcIso).subscribe(response => {
      this.generatedToken = response?.Token || '';
      this.isKeyCopied = false;
      this.isGenerating = false;
      this.tokenLabel = '';
      this.toastr.success('Personal API key generated successfully.', 'Success', this.toasterConfig);
      this.loadTokens();
    }, (error) => {
      this.isGenerating = false;
      this.toastr.error(this.getErrorMessage(error, 'Unable to generate API key.'), 'Error', this.toasterConfig);
    });
  }

  revokeToken(token: any): void {
    // Modified by Karanam on Oct 09 2026 to fix code review comment S-09/A-08: guard in addition to the disabled button.
    if (!this.canRevokeKey) {
      return;
    }

    if (!token || token.IsRevoked || token.Status === 'Expired') {
      return;
    }

    // Revoke is irreversible for the current token record.
    // Modified by Karanam on Oct 09 2026 to fix code review comment F-01 (spec 6.2): revoke confirmation copy. Original line commented out below.
    // const confirmed = window.confirm('Revoke this token? This action cannot be undone.');
    const confirmed = window.confirm("Revoke this key? Any AI tool currently using it will lose access immediately. This can't be undone \u2014 reconnecting will require a new key.");
    if (!confirmed) {
      return;
    }

    this.revokingTokenId = token.TokenId;
    // Modified by Karanam on Oct 09 2026 to fix code review comment S-17/S-18: no targetUserId.
    // this.personalApiKeyService.revokeToken(token.TokenId, this.targetUserId || undefined).subscribe(() => {
    this.personalApiKeyService.revokeToken(token.TokenId).subscribe(() => {
      this.revokingTokenId = null;
      this.toastr.success('Token revoked successfully.', 'Success', this.toasterConfig);
      this.loadTokens();
    }, (error) => {
      this.revokingTokenId = null;
      this.toastr.error(this.getErrorMessage(error, 'Unable to revoke token.'), 'Error', this.toasterConfig);
    });
  }

  copyToken(): void {
    if (!this.generatedToken) {
      return;
    }

    navigator.clipboard.writeText(this.generatedToken).then(() => {
      this.isKeyCopied = true;
      this.toastr.info('Token copied to clipboard.', 'Info', this.toasterConfig);
    }).catch(() => {
      this.toastr.warning('Could not copy token. Please copy manually.', 'Warning', this.toasterConfig);
    });
  }

  // Modified by Karanam on Oct 09 2026 to fix code review comment B-05: removed the Claude Desktop config snippet (copyClaudeConfigSnippet and claudeConfigSnippet); it was non-functional (no server URL) and the server URL is shared with the client separately.
  //   copyClaudeConfigSnippet(): void {
  //     const snippet = this.claudeConfigSnippet;
  //     if (!snippet) {
  //       return;
  //     }
  //
  //     navigator.clipboard.writeText(snippet).then(() => {
  //       this.isKeyCopied = true;
  //       this.toastr.info('Claude Desktop snippet copied.', 'Info', this.toasterConfig);
  //     }).catch(() => {
  //       this.toastr.warning('Could not copy snippet. Please copy manually.', 'Warning', this.toasterConfig);
  //     });
  //   }
  //
  //   get claudeConfigSnippet(): string {
  //     if (!this.generatedToken) {
  //       return '';
  //     }
  //
  //     // Keep snippet aligned with the current generated token shown one time to the user.
  //     return `{
  //   "mcpServers": {
  //     "storage-suite": {
  //       "command": "npx",
  //       "args": ["-y", "@anthropic-ai/mcp-client"],
  //       "env": {
  //         "PERSONAL_API_KEY": "${this.generatedToken}"
  //       }
  //     }
  //   }
  // }`;
  //   }

  formatDisplayDate(value: string | Date): string {
    if (!value) {
      return '-';
    }

    return new Date(value).toLocaleString();
  }

  maskKeyPrefix(prefix: string): string {
    if (!prefix) {
      return '-';
    }

    return `ssm_live_${prefix}...`;
  }

  trackByTokenId(index: number, item: any): number {
    return item.TokenId;
  }

  private getExpiryDateUtcIso(): string {
    // Modified by Karanam on Oct 09 2026 to fix code review comment B-04: expiry was set to the browser's LOCAL end of day but sent as UTC; it is now computed as end of the UTC day. Original lines commented out below.
    // // Expiry is stored as end-of-day UTC based on selected duration.
    // const days = Number(this.expirySelection || '365');
    // const expires = new Date();
    // expires.setDate(expires.getDate() + days);
    // expires.setHours(23, 59, 59, 0);
    // return expires.toISOString();
    const days = Number(this.expirySelection || '365');
    const now = new Date();
    const expires = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days, 23, 59, 59, 0));
    return expires.toISOString();
  }

  private hasPendingChanges(): boolean {
    // Pending state includes unsaved label edits and uncopied generated secrets.
    return !!(this.tokenLabel?.trim() || (this.generatedToken && !this.isKeyCopied));
  }

  private canCloseWindow(): boolean {
    if (!this.hasPendingChanges()) {
      return true;
    }

    const confirmationMessage = this.generatedToken && !this.isKeyCopied
      ? 'Generated key has not been copied yet. Closing now will hide it permanently. Do you want to close anyway?'
      : 'You have unsaved changes. Do you want to close without saving?';

    return window.confirm(confirmationMessage);
  }

  private getErrorMessage(error: any, fallback: string): string {
    if (error?.status === 401 || error?.status === 403) {
      return 'You do not have permission to view or manage personal API keys for this user.';
    }

    // Prefer server error text when available, otherwise use generic fallback.
    const apiError = error?.error;
    if (typeof apiError === 'string' && apiError.trim()) {
      return apiError;
    }

    if (apiError?.message && typeof apiError.message === 'string') {
      return apiError.message;
    }

    if (apiError?.Message && typeof apiError.Message === 'string') {
      return apiError.Message;
    }

    if (error?.message && typeof error.message === 'string') {
      return error.message;
    }

    return fallback;
  }
}
